import React, { useState } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Tooltip, alpha, useTheme } from '@mui/material';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';

/**
 * Reusable export button for table data.
 *
 * Props:
 *   data     – Array of row objects (the current table data)
 *   columns  – Array of { label, key, value? } describing CSV columns.
 *              `key` is the dot-path field name (e.g. 'user.name').
 *              `value(row)` is an optional formatter that returns the cell string.
 *   filename – Base filename without extension (default: 'export')
 *   disabled – Disables the button entirely
 *   size     – MUI button size (default: 'small')
 *   variant  – 'icon' for compact icon-only, 'button' for full button (default: 'button')
 */

const resolvePath = (obj, path) => {
  if (!path) return '';
  return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
};

const escapeCell = (val) => {
  if (val == null) return '';
  const str = String(val).replace(/"/g, '""');
  return /[",\n\r]/.test(str) ? `"${str}"` : str;
};

const buildCsv = (data, columns) => {
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const rows = data.map((row) =>
    columns
      .map((col) => {
        const raw = col.value ? col.value(row) : resolvePath(row, col.key);
        return escapeCell(raw);
      })
      .join(','),
  );
  return `${header}\n${rows.join('\n')}`;
};

const buildJson = (data, columns) => {
  const mapped = data.map((row) => {
    const obj = {};
    columns.forEach((col) => {
      obj[col.label] = col.value ? col.value(row) : resolvePath(row, col.key);
    });
    return obj;
  });
  return JSON.stringify(mapped, null, 2);
};

const triggerDownload = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function ExportTableButton({
  data = [],
  columns = [],
  filename = 'export',
  disabled = false,
  size = 'small',
  variant = 'button',
  onExportAll,
  total,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const open = Boolean(anchorEl);

  const rowCount = total != null ? Number(total) : data.length;
  const isDisabled = disabled || rowCount === 0;

  const handleClick = (event) => {
    if (isDisabled || exporting) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleExport = async (format) => {
    handleClose();
    setExporting(true);

    // Tiny delay so UI shows the spinner before the synchronous build runs
    await new Promise((r) => setTimeout(r, 50));

    try {
      let exportRows = data;
      if (typeof onExportAll === 'function') {
        const fetched = await onExportAll();
        if (Array.isArray(fetched)) {
          exportRows = fetched;
        }
      }

      if (!exportRows || exportRows.length === 0) {
        return;
      }

      const timestamp = new Date().toISOString().slice(0, 10);
      if (format === 'csv') {
        triggerDownload(buildCsv(exportRows, columns), `${filename}_${timestamp}.csv`, 'text/csv;charset=utf-8');
      } else {
        triggerDownload(buildJson(exportRows, columns), `${filename}_${timestamp}.json`, 'application/json');
      }
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const secondaryLabel = onExportAll
    ? (total != null ? `All ${Number(total).toLocaleString('en-IN')} rows (Full Export)` : 'All matching rows (Full Export)')
    : `${data.length} rows`;

  const tooltipTitle = isDisabled
    ? 'No data to export'
    : exporting
    ? 'Exporting full dataset...'
    : onExportAll && total != null
    ? `Export all ${Number(total).toLocaleString('en-IN')} rows`
    : 'Export table data';

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <Button
            size={size}
            variant="outlined"
            color="inherit"
            startIcon={
              exporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Download size={16} />
              )
            }
            onClick={handleClick}
            disabled={isDisabled || exporting}
            sx={{
              borderRadius: 1.5,
              fontWeight: 600,
              fontSize: '0.8125rem',
              textTransform: 'none',
              borderColor: theme.palette.divider,
              color: 'text.secondary',
              px: 1.5,
              minWidth: variant === 'icon' ? 'auto' : undefined,
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            {variant === 'icon' ? null : (exporting ? 'Exporting…' : 'Export')}
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 2,
              minWidth: 200,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            },
          },
        }}
      >
        <MenuItem onClick={() => handleExport('csv')} sx={{ py: 1.25, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <FileSpreadsheet size={18} color={theme.palette.success.main} />
          </ListItemIcon>
          <ListItemText
            primary="Export as CSV"
            secondary={secondaryLabel}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleExport('json')} sx={{ py: 1.25, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <FileText size={18} color={theme.palette.info.main} />
          </ListItemIcon>
          <ListItemText
            primary="Export as JSON"
            secondary={secondaryLabel}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
